import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import { listItems, type BrowseItem } from './fileBrowserApi';
import '../documentEditor/DocumentOpenPickerPopup.css';

export type MyStorageFolderPickerPopupProps = {
  visible: boolean;
  onClose: () => void;
  /** Selected relative folder path under My Storage (empty string = root). */
  onSelect: (folderPath: string) => void;
  initialPath?: string;
};

type Crumb = { label: string; path: string };

export function MyStorageFolderPickerPopup({
  visible,
  onClose,
  onSelect,
  initialPath = ''
}: MyStorageFolderPickerPopupProps) {
  const popupOptions = useResponsivePopupOptions({ width: 720, height: 560 });
  const [folderPath, setFolderPath] = useState('');
  const [items, setItems] = useState<BrowseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crumbs = useMemo((): Crumb[] => {
    const parts: Crumb[] = [{ label: 'My Storage', path: '' }];
    if (!folderPath) return parts;
    const segments = folderPath.split('/').filter(Boolean);
    let accum = '';
    for (const segment of segments) {
      accum = accum ? `${accum}/${segment}` : segment;
      parts.push({ label: segment, path: accum });
    }
    return parts;
  }, [folderPath]);

  const loadFolder = useCallback(async (nextPath: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = await listItems(USER_STORAGE_MOUNT_ID, nextPath || undefined);
      const folders = next
        .filter((item) => item.isDirectory)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      setItems(folders);
      setFolderPath(nextPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not list folder.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    void loadFolder(initialPath.replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, ''));
  }, [visible, initialPath, loadFolder]);

  const onCrumbClick = useCallback(
    (crumb: Crumb) => {
      void loadFolder(crumb.path);
    },
    [loadFolder]
  );

  const selectCurrent = useCallback(() => {
    onSelect(folderPath);
  }, [folderPath, onSelect]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      showTitle
      title="Choose default folder"
      showCloseButton
      dragEnabled={!popupOptions.fullScreen}
      hideOnOutsideClick={false}
      {...popupOptions}
      wrapperAttr={{ class: 'jarvis-doc-open-picker-popup' }}
      position={{ my: 'center', at: 'center', of: window }}
    >
      <div className="jarvis-doc-open-picker">
        <div className="jarvis-doc-open-picker__toolbar">
          <nav className="jarvis-doc-open-picker__crumbs" aria-label="Folder path">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`}>
                {index > 0 ? <span className="jarvis-doc-open-picker__crumb-sep">/</span> : null}
                <button
                  type="button"
                  className="jarvis-doc-open-picker__crumb"
                  disabled={index === crumbs.length - 1}
                  onClick={() => onCrumbClick(crumb)}
                >
                  {crumb.label}
                </button>
              </span>
            ))}
          </nav>
        </div>

        {error ? <p className="jarvis-doc-open-picker__error">{error}</p> : null}

        <div className="jarvis-doc-open-picker__list" role="listbox" aria-label="Folders">
          {loading ? <p className="jarvis-doc-open-picker__muted">Loading…</p> : null}
          {!loading
            ? items.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  role="option"
                  className="jarvis-doc-open-picker__row"
                  onClick={() => void loadFolder(item.path)}
                  onDoubleClick={() => void loadFolder(item.path)}
                >
                  <span className="jarvis-doc-open-picker__row-icon dx-icon dx-icon-folder" />
                  <span className="jarvis-doc-open-picker__row-name">{item.name}</span>
                </button>
              ))
            : null}
          {!loading && !error && items.length === 0 ? (
            <p className="jarvis-doc-open-picker__empty">
              No subfolders here. You can still select this folder.
            </p>
          ) : null}
        </div>

        <div className="jarvis-doc-open-picker__footer">
          <Button text="Cancel" stylingMode="outlined" onClick={onClose} />
          <Button text="Select this folder" type="default" onClick={selectCurrent} />
        </div>
      </div>
    </Popup>
  );
}
